import { Item } from '@/api/items'
import {
  NativeFulfillmentAttributes,
  NativeVarDataAttributes
} from '@/api/commerce/proof'

export enum SurfaceObjectIdPrefix {
  ItemSurface = 'S',
  ItemSurfaceArtwork = 'A',
  AssetImage = 'AI',
  CustomerImage = 'CI',
  ImageContainer = 'IC',
  ItemSurfaceFoil = 'F',
  MaskBleed = 'MB',
  MaskImage = 'MI',
  Phrase = 'P',
  RemoteImage = 'RI',
  TextBox = 'TB',
  BackStrip = 'BS'
}

export type SurfaceRequest = {
  item: Pick<Item, 'code' | 'modifyTime' | 'seoPath'>
  itemSurfaceCode?: string
  itemSurfaceAssetVersion?: string
  size?: number
  dpr?: number
  varDataAttributes?: NativeVarDataAttributes
  fulfillmentAttributes?: NativeFulfillmentAttributes
  prefixes?: SurfaceObjectIdPrefix[]
  ids?: string[]
  crop?: boolean
  cropBox?: [number, number, number, number]
  extension?: 'jpg' | 'png' | 'svg'
}

const extractSeoPrefix = (seoPath: string): string => {
  const regex = /^\/eco\/(.*?)-p-.*\.html$/
  const match = seoPath.match(regex)

  if (match && match[1]) {
    return match[1]
  } else {
    return ''
  }
}

export const itemSurfaceUrl = (request: SurfaceRequest): string => {
  // These are all required to exist
  const item = request.item
  const code = item.code
  const seoString = extractSeoPrefix(item.seoPath)
  const modifyTimeSec = item.modifyTime / 1000

  // Required by the API, but has a default
  const extension = request.extension ?? 'jpg'

  // Build path filename segments
  const pathFilenameParts: string[] = [seoString, String(modifyTimeSec)]
  if (request.itemSurfaceCode && request.itemSurfaceAssetVersion) {
    pathFilenameParts.push(
      request.itemSurfaceCode,
      request.itemSurfaceAssetVersion
    )
  }
  pathFilenameParts.push(extension)

  // The query param string
  const searchParams = new URLSearchParams()
  if (request.crop === false) {
    searchParams.append('crop', '0')
  }
  if (request.cropBox) {
    searchParams.append('cropBox', request.cropBox.join(','))
  }
  if (request.size) {
    searchParams.append('s', String(request.size))
  }
  if (request.dpr && request.dpr !== 1) {
    searchParams.append('dpr', String(request.dpr))
  }
  // Multi param strings
  request.prefixes?.forEach((prefix) => searchParams.append('prefixes', prefix))
  request.ids?.forEach((id) => searchParams.append('ids', id))
  // JS objects that must be stringified
  if (
    request.fulfillmentAttributes &&
    Object.keys(request.fulfillmentAttributes).length
  ) {
    searchParams.append('fA', JSON.stringify(request.fulfillmentAttributes))
  }
  if (
    request.varDataAttributes &&
    Object.keys(request.varDataAttributes).length
  ) {
    searchParams.append('vDA', JSON.stringify(request.varDataAttributes))
  }
  // Append query string if not empty
  const searchParamsString = searchParams.toString()
  const searchParamsSuffix = searchParamsString.length
    ? '?' + searchParamsString
    : ''

  return `/api/v3/items/${code}/${pathFilenameParts.join(
    '.'
  )}${searchParamsSuffix}`
}
